import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import Navigation from '../components/Navigation';
import Footer from '../../Landing/footer';
import { blogPosts, BlogPost } from '../../../data/blogPosts';
import BlogCard from './components/BlogCard';
import { Search } from 'lucide-react';

const Page = styled.div`
  min-height: 100vh;
  background: #f9fafb;
`;

const Header = styled.div`
  background: #ffffff;
  padding: 80px 0 60px;
  border-bottom: 1px solid #e5e7eb;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #6b7280;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.6;
`;

const SearchBar = styled.div`
  max-width: 500px;
  margin: 0 auto;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 24px 16px 50px;
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  font-size: 16px;
  outline: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  &:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 32px;
  padding: 60px 0 100px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 0;
  color: #6b7280;
  font-size: 18px;
`;

const BlogIndex: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [dynamicPosts, setDynamicPosts] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const calculateReadTime = (text: string) => {
        if (!text) return "1 min read";
        const wordCount = text.trim().split(/\s+/).length;
        const readTimeMinutes = Math.ceil(wordCount / 225);
        return `${readTimeMinutes} min read`;
    };

    useEffect(() => {
        const fetchDynamicPosts = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                const response = await fetch(`${baseUrl}/api/outrank/articles`);
                if (response.ok) {
                    const data = await response.json();
                    const formattedPosts: BlogPost[] = data.map((article: any) => ({
                        id: article.slug,
                        slug: article.slug,
                        title: article.title,
                        description: article.meta_description || '',
                        date: article.published_at ? new Date(article.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                        author: 'Asif Farhan Khan',
                        category: 'Blog',
                        readTime: calculateReadTime(article.content_markdown),
                        tags: article.tags || [],
                        imageUrl: article.image_url,
                        type: 'markdown'
                    }));
                    setDynamicPosts(formattedPosts);
                }
            } catch (error) {
                console.error("Failed to fetch dynamic articles:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDynamicPosts();
    }, []);

    const allPosts = [...blogPosts, ...dynamicPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const filteredPosts = allPosts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Page>
            <Helmet>
                <title>Blog | SmushChat - AI Automation Insights</title>
                <meta name="description" content="Insights, tutorials, and industry commentary on AI automation, chatbots, and customer service." />
            </Helmet>
            <Navigation />

            <Header>
                <Container>
                    <Title>SmushLabs Blog</Title>
                    <Subtitle>
                        Insights, tutorials, and industry commentary on AI automation, chatbots, and customer service.
                    </Subtitle>
                    <SearchBar>
                        <SearchIcon size={20} />
                        <SearchInput
                            type="text"
                            placeholder="Search articles..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </SearchBar>
                </Container>
            </Header>

            <Container>
                {isLoading ? (
                    <NoResults>Loading articles...</NoResults>
                ) : filteredPosts.length > 0 ? (
                    <Grid>
                        {filteredPosts.map(post => (
                            <BlogCard key={post.id} post={post} />
                        ))}
                    </Grid>
                ) : (
                    <NoResults>
                        No articles found matching "{searchQuery}"
                    </NoResults>
                )}
            </Container>

            <Footer />
        </Page>
    );
};

export default BlogIndex;
